import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, Clock, CheckCircle2, CalendarPlus, Video } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const keyStore = (slug) => `webinar_key_${slug}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }); } catch { return iso; } };

// Public registration landing page: /w/:slug
export default function WebinarPublic() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ first_name: '', last_name: '', email: '', mobile: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { axios.get(`${API}/api/webinars/public/${slug}`).then((r) => setD(r.data)).catch(() => setError('This webinar link is not valid.')); }, [slug]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/api/webinars/public/${slug}/register`, { ...f, base_url: window.location.origin });
      localStorage.setItem(keyStore(slug), data.join_key); setDone(data);
    } catch (err) { setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Please check your details and try again.'); } finally { setBusy(false); }
  };
  if (error && !d) return <div className="grid min-h-screen place-items-center bg-slate-50 p-6 text-sm text-slate-600" data-testid="webinar-public-error">{error}</div>;
  if (!d) return <div className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-400">Loading…</div>;
  const w = d.webinar;
  const live = w.status === 'live';
  const input = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm outline-none focus:border-slate-400';
  return (
    <div className="min-h-screen bg-[#F3F5F9]" data-testid="webinar-public-page">
      <div className="h-1.5" style={{ background: w.brand_color }} />
      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:py-16">
        <div>
          <p className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-black uppercase tracking-wider text-white" style={{ background: w.brand_color }}><Video className="h-3.5 w-3.5" />{live ? 'Live now' : w.status === 'ended' ? 'Webinar finished' : 'Free live webinar'}</p>
          <h1 className="mt-4 text-4xl font-black leading-tight tracking-tight text-slate-900 sm:text-5xl" data-testid="webinar-public-title">{w.title}</h1>
          {w.presenter_name && <p className="mt-3 text-base font-semibold text-slate-600">with {w.presenter_name}</p>}
          <div className="mt-6 space-y-2 text-sm text-slate-700">
            <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" />{fmt(w.starts_at)}</p>
            <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" />{w.duration_min} minutes</p>
          </div>
          {w.description && <p className="mt-6 whitespace-pre-line text-base leading-relaxed text-slate-600">{w.description}</p>}
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-xl ring-1 ring-slate-200 sm:p-8">
          {done ? (
            <div className="text-center" data-testid="webinar-registered">
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
              <h2 className="mt-3 text-2xl font-black text-slate-900">You're in{done.already ? ' already' : ''}!</h2>
              <p className="mt-2 text-sm text-slate-600">We've sent your personal join link by email{f.mobile ? ' and text' : ''}. We'll remind you an hour before.</p>
              <div className="mt-5 grid gap-2">
                <button type="button" onClick={() => nav(`/w/${slug}/live?k=${done.join_key}`)} data-testid="webinar-join-now" className="rounded-xl py-3 text-sm font-black text-white" style={{ background: w.brand_color }}>{live ? 'Join now' : 'Go to the webinar room'}</button>
                <a href={`${API}/api/webinars/public/${slug}/calendar.ics`} data-testid="webinar-add-calendar" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-700"><CalendarPlus className="h-4 w-4" />Add to calendar</a>
              </div>
            </div>
          ) : w.status === 'ended' ? (
            <p className="text-center text-sm text-slate-600">This webinar has finished. Thanks for your interest.</p>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="webinar-register-form">
              <h2 className="text-xl font-black text-slate-900">Save your seat</h2>
              <div className="grid grid-cols-2 gap-3">
                <input required value={f.first_name} onChange={set('first_name')} placeholder="First name" className={input} data-testid="reg-first-name" />
                <input value={f.last_name} onChange={set('last_name')} placeholder="Last name" className={input} data-testid="reg-last-name" />
              </div>
              <input required type="email" value={f.email} onChange={set('email')} placeholder="Email" className={input} data-testid="reg-email" />
              <input type="tel" value={f.mobile} onChange={set('mobile')} placeholder="Mobile (for a text reminder)" className={input} data-testid="reg-mobile" />
              {error && <p className="text-sm text-rose-600" data-testid="reg-error">{error}</p>}
              <button type="submit" disabled={busy} className="w-full rounded-xl py-3.5 text-sm font-black text-white disabled:opacity-60" style={{ background: w.brand_color }} data-testid="reg-submit">{busy ? 'Registering…' : 'Register free'}</button>
              <p className="text-center text-[11px] text-slate-400">By registering you agree to receive emails and texts about this webinar.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
